import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ClockIcon,
  ExclamationIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/outline";
import { getBusinessLifecycle } from "../../../../Constants/business";

// Severity -> tone. Mirrors the house `Alert` component exactly (bg-50 /
// border-200 / icon-400 / title-800 / body-700) so the lifecycle strip reads as
// one of the app's own notices — a soft tinted surface with a single hairline
// border, not a shouting left-rail alarm. The action is a white secondary
// button in-tone (the house Button `secondary` shape) rather than a saturated
// solid, keeping the strip informative and calm.
const TONES = {
  warning: {
    wrap: "bg-amber-50 border-amber-200",
    icon: "text-amber-400",
    title: "text-amber-800",
    body: "text-amber-700",
    meta: "text-amber-700",
    button:
      "border-amber-300 bg-white text-amber-800 hover:bg-amber-100 focus:ring-amber-500",
    Icon: ClockIcon,
  },
  restricted: {
    wrap: "bg-orange-50 border-orange-200",
    icon: "text-orange-400",
    title: "text-orange-800",
    body: "text-orange-700",
    meta: "text-orange-700",
    button:
      "border-orange-300 bg-white text-orange-800 hover:bg-orange-100 focus:ring-orange-500",
    Icon: ExclamationIcon,
  },
  danger: {
    wrap: "bg-red-50 border-red-200",
    icon: "text-red-400",
    title: "text-red-800",
    body: "text-red-700",
    meta: "text-red-700",
    button:
      "border-red-300 bg-white text-red-800 hover:bg-red-100 focus:ring-red-500",
    Icon: ExclamationCircleIcon,
  },
};

// Per-state copy, keyed by status + lineage. English-as-key strings (matches the
// surrounding pages); i18n falls back to these when a translation is absent.
const COPY = {
  INACTIVE_WARNED: {
    title: "This business is inactive",
    body: "No transactions for 3 months. Record a transaction to keep it active and avoid it being scheduled for deletion.",
    countdownLabel: "Deactivated in",
    recordCta: "Record Transaction",
  },
  RESTRICTED: {
    title: "This business is restricted",
    body: "Free accounts can keep only 1 active business. This one is read-only until you subscribe to premium.",
    countdownLabel: "Scheduled for deletion in",
    subscribeCta: "Subscribe to Premium",
  },
  PENDING_FREE_LIMIT: {
    title: "This business is scheduled for deletion",
    body: "Its grace period ended without a premium subscription. Subscribe to premium to restore it before its data is removed.",
    countdownLabel: "Deactivated on",
    subscribeCta: "Subscribe to Premium",
  },
  PENDING_INACTIVITY: {
    title: "This business is scheduled for deletion",
    body: "It was deactivated after a long period without activity. Record a transaction to restore it before its data is removed.",
    countdownLabel: "Deactivated on",
    recordCta: "Record Transaction to Restore",
  },
};

const copyFor = (life) => {
  if (life.status === "INACTIVE_WARNED") return COPY.INACTIVE_WARNED;
  if (life.status === "RESTRICTED") return COPY.RESTRICTED;
  return life.isFreeLimit ? COPY.PENDING_FREE_LIMIT : COPY.PENDING_INACTIVITY;
};

/**
 * LifecycleRibbon — the single, consistent surface that tells a user their
 * business's lifecycle state, the concrete deadline (date + days-left), and the
 * one action that resolves it. Rendered in the accounting shells and on the
 * write pages.
 *
 * Props:
 *  - business: the business record (needs status + lifecycle markers).
 *  - onRecord: optional handler for the "record transaction" CTA. When omitted,
 *      the record button is hidden (e.g. on a create page where the user is
 *      already recording) — the informative message still shows.
 *  - onSubscribe: optional; defaults to navigating to /subscription.
 *  - className: extra classes for layout context.
 */
const BusinessStatusBanner = ({
  business,
  onRecord,
  onSubscribe,
  className = "",
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const life = getBusinessLifecycle(business);
  if (!life) return null; // ACTIVE / healthy -> nothing to show

  const tone = TONES[life.severity];
  const copy = copyFor(life);
  const { Icon } = tone;

  const goSubscribe = onSubscribe || (() => navigate("/subscription"));

  // Countdown line, sentence-case and calm — the days-left is the only word that
  // gets weight, so it scans without shouting. "<label> 23 days · 14 Aug 2026",
  // or a plain date for PENDING.
  let countdown = null;
  if (life.deadline) {
    const date = dayjs(life.deadline).locale(i18n.language).format("DD MMM YYYY");
    if (life.status === "PENDING_DELETION") {
      countdown = (
        <>
          {t(copy.countdownLabel)} {date}
        </>
      );
    } else if (life.daysLeft != null) {
      const unit = life.daysLeft === 1 ? t("day") : t("days");
      countdown = (
        <>
          {t(copy.countdownLabel)}{" "}
          <span className="font-semibold">
            {life.daysLeft} {unit}
          </span>{" "}
          · {date}
        </>
      );
    }
  }

  const showSubscribe = life.ctaKind === "subscribe";
  const showRecord = life.ctaKind === "record" && typeof onRecord === "function";

  return (
    <div
      className={`rounded-lg border p-4 ${tone.wrap} ${className}`}
      role="status"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <Icon
            className={`h-5 w-5 flex-shrink-0 mt-0.5 ${tone.icon}`}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <p className={`text-sm font-medium ${tone.title}`}>
              {t(copy.title)}
            </p>
            <p className={`mt-0.5 text-sm ${tone.body}`}>{t(copy.body)}</p>
            {countdown && (
              <p className={`mt-1.5 text-xs ${tone.meta}`}>{countdown}</p>
            )}
          </div>
        </div>

        {(showSubscribe || showRecord) && (
          <div className="flex-shrink-0 sm:pl-3">
            <button
              type="button"
              onClick={showSubscribe ? goSubscribe : onRecord}
              className={`inline-flex w-full items-center justify-center rounded-md border px-3.5 py-2 text-sm font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 sm:w-auto ${tone.button}`}
            >
              {showSubscribe ? t(copy.subscribeCta) : t(copy.recordCta)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessStatusBanner;
